import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useThree } from "@react-three/fiber";
 import { useProgress } from "@react-three/drei";
import { useCreateRendererApi } from "renderer/hooks/useCreateRendererApi";
import { VIEWER_MODES } from "../../consts";
import {
	usePostMessageConfig,
	useConfigControls,
	useConfigModelUrl,
	useConfigRenderer,
	useConfigShowProgress,
	useHasConfig,
	useConfigTakeScreenshot,
	useApplyConfigImmediate, useConfigCloudName,
} from "../../hooks/config";
import useAnalytics from "../../hooks/useAnalytics";
import useViewerZoom from "../../hooks/useViewerZoom";
import ViewerButtons from "../ViewerButtons";
import LoadProgressInfo from "../LoadProgressInfo";
import LoadSpinner from "../LoadSpinner";
import Empty from "../Empty";
import ViewerCanvas from "../ViewerCanvas";
import { ModelProductConfigFromState } from "../ModelProductConfig";
import ModelHotspots from "../ModelHotspots";
import useModelEventsManager from "../../hooks/useModelEventsManager";
import { getModelUrlWithConfigFromQuery, getModelUrlWithConfigFromState } from "../../utils/arUtils";

const ViewerReady = ({ getIsReady, onRenderingReady }) => {
	const { gl, scene, camera } = useThree();
	const isReady = getIsReady();

	useEffect(() => {
		if (isReady) {
			onRenderingReady(gl, scene, camera, true);
		}
	}, [isReady]);

	return null;
};

const BaseViewer = ({
	                    mode,
	                    modelUrl,
	                    showLoadProgress,
	                    takeScreenshot,
	                    triggerAnalyticsEvent,
	                    ANALYTICS_EVENTS,
	                    panRotate,
	                    rendererConfig = {},
	                    ProductConfigApplier,
	                    onRenderingReady,
	                    onModelReady,
	                    renderingApi,
                    }) => {
	const cloudName = useConfigCloudName();
	const { zoomDirection, zoomIn, zoomOut } = useViewerZoom();
	const applyConfigImmediate = useApplyConfigImmediate();
	const [progress, setProgress] = useState({ loaded: 0, total: 0 });
	//need to use ref and not state in order to work with textures!!!!
	const isReadyRef = useRef(false);
	const hasModel = !!modelUrl;
	const modelUrlWithConfig =
		(applyConfigImmediate && (mode === VIEWER_MODES.QR)) ?
			getModelUrlWithConfigFromQuery(modelUrl, cloudName) :
			((applyConfigImmediate && (mode === VIEWER_MODES.FULL)) ?
				getModelUrlWithConfigFromState(modelUrl, cloudName) :
				modelUrl);

	const usingImmediateConfig = !!applyConfigImmediate;
	const isProgressDone = useMemo(() => (progress.loaded / progress.total) === 1, [progress]);
	const activeArtifactLoading = useProgress((state) => state.active);
	const activeItemLoading = useProgress((state) => state.item);
	const modelEventsManager = useModelEventsManager();

	const setIsReady = useCallback((val) => {
		isReadyRef.current = val;
	}, []);

	useEffect(() => {
		setIsReady(isProgressDone);
	}, [setIsReady, isProgressDone])

	useEffect(() => {
		triggerAnalyticsEvent?.(ANALYTICS_EVENTS.VIEWER_INIT, { hasModel });
	}, []);

	useEffect(() => {
		if (modelUrl) {
			setProgress({ loaded: 0, total: 0 });
			triggerAnalyticsEvent?.(ANALYTICS_EVENTS.MODEL_INIT, { modelUrl, usingImmediateConfig });
		}
	}, [modelUrl, usingImmediateConfig]);

	useEffect(() => {
		if (isProgressDone) {
			triggerAnalyticsEvent?.(ANALYTICS_EVENTS.MODEL_LOADED, { modelUrl, usingImmediateConfig });
		}
	}, [isProgressDone, modelUrl, usingImmediateConfig]);

	//TODO: catch model load ERROR and send analytics event

	const getIsReady = useCallback(() => isReadyRef.current, []);

	const ModelContent = useMemo(() => {
		return (
			<>
				{!usingImmediateConfig && <ProductConfigApplier getIsReady={getIsReady} renderingApi={renderingApi}/>}
				{mode === VIEWER_MODES.FULL && <ModelHotspots getIsReady={getIsReady} renderingApi={renderingApi} modelEventsManager={modelEventsManager}/>}
				<ViewerReady getIsReady={getIsReady} onRenderingReady={onRenderingReady}/>
			</>
		);
	}, [getIsReady, onRenderingReady, renderingApi, isReadyRef.current, usingImmediateConfig])

	return (
		<>
			{hasModel && <ViewerCanvas
					mode={mode}
					modelUrl={modelUrlWithConfig}
					takeScreenshot={takeScreenshot}
					setProgress={setProgress}
					zoomDirection={zoomDirection}
					onModelReady={onModelReady}
					panRotate={panRotate}
					rendererConfig={rendererConfig}
					modelContent={ModelContent}
					modelEventsManager={modelEventsManager}
				>
					{/*TODO - bring back loading sphere when model loading is suspensed correctly*/}
					{/*<LoaderSphere/>*/}
				</ViewerCanvas>}

			{isReadyRef.current && mode !== VIEWER_MODES.QR &&
				<ViewerButtons
					zoomIn={zoomIn}
					zoomOut={zoomOut}
					renderingApi={renderingApi}
				/>}
			{hasModel ?
				<>
					{isProgressDone && activeArtifactLoading && <LoadSpinner key={activeItemLoading}/>}
					{showLoadProgress && <LoadProgressInfo progress={progress} isReady={isProgressDone}/>}
				</>
				 :
				<Empty label="Provide a model to show"/>
			}
		</>
	);
};

const ConfigViewer = () => {
	const [renderingApi, setRenderingApi] = useState(null);
	const modelUrl = useConfigModelUrl();
	const { triggerAnalyticsEvent, ANALYTICS_EVENTS } = useAnalytics();
	const showLoadProgress = useConfigShowProgress();
	const takeScreenshot = useConfigTakeScreenshot();
	const { pan, rotate } = useConfigControls();
	const rendererConfig = useConfigRenderer();
	const { onModelReady, onRenderingReady } = useCreateRendererApi(modelUrl, setRenderingApi);
	const { setRendererApi } = usePostMessageConfig();

	useEffect(() => {
		//provide the rendering api to the viewer Widget PM api to support model-related functionality (ex: export gltf)
		setRendererApi(renderingApi);
	}, [renderingApi]);

	return (
		<BaseViewer
			modelUrl={modelUrl}
			showLoadProgress={showLoadProgress}
			takeScreenshot={takeScreenshot}
			panRotate={pan || rotate}
			rendererConfig={rendererConfig}
			triggerAnalyticsEvent={triggerAnalyticsEvent}
			ANALYTICS_EVENTS={ANALYTICS_EVENTS}
			mode={VIEWER_MODES.FULL}
			ProductConfigApplier={ModelProductConfigFromState}
			onModelReady={onModelReady}
			renderingApi={renderingApi}
			onRenderingReady={onRenderingReady}
		/>
	);
};

const Viewer = ({}) => {
	const hasConfig = useHasConfig();

	return (
		hasConfig ? <ConfigViewer/> : <Empty/>
	);
};

export default Viewer;

export {
	BaseViewer,
};
